import React from "react";
import {
    PDFDownloadLink,
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet,
    Font,
} from "@react-pdf/renderer";
import CenturyGothicFont from "Assets/fonts/centurygothic.ttf";
import CenturyGothicFontBold from "Assets/fonts/centurygothic_bold.ttf";
import { renderHtmlToReactPdf } from "../SbJobDetailsComponent/SbJobDetailsSubComponents/utils/RenderHtmlToReactPdf";
import { minimizeHtmlSpacing, removeHeaderImageBlock } from "../SbJobDetailsComponent/SbJobDetailsSubComponents/utils/MinimizeHtmlSpacing";
import useJobsStore from "Store/useJobsStore";
import useMermaidPdfImages from "Hooks/useMermaidPdfImages";
import { marked } from "marked";
import { SbButton } from "uiCore";
import DownloadIcon from "Components/SBLayoutIcon/DownloadIcon";

/* ─── Font Registration ─── */

Font.register({
    family: "CenturyGothic",
    fonts: [
        { src: CenturyGothicFont, fontWeight: "normal" },
        { src: CenturyGothicFontBold, fontWeight: "bold" },
        { src: CenturyGothicFont, fontStyle: "italic" },
    ],
});

/* ─── Stylesheet ─── */

const styles = StyleSheet.create({
    page: {
        flexDirection: "column",
        padding: 30,
        fontFamily: "CenturyGothic",
        fontSize: 10,
        lineHeight: 1.4,
        position: "relative",
    },
    content: {},
    text: {
        fontFamily: "CenturyGothic",
        fontSize: 10,
        lineHeight: 1.4,
    },
    watermark: {
        position: "absolute",
        top: "40%",
        left: 0,
        right: 0,
        textAlign: "center",
        fontSize: 100,
        color: "grey",
        opacity: 0.2,
        transform: "rotate(-45deg)",
        zIndex: -1,
    },
    section: {
        marginTop: 10,
        marginBottom: 0,
    },
    disclaimerHeading: {
        fontSize: 13,
        fontWeight: "bold",
        fontFamily: "CenturyGothic",
        marginBottom: 4,
    },
    disclaimer: {
        marginTop: 3,
        marginBottom: 1,
        fontFamily: "CenturyGothic",
        color: "#2B3034",
        fontSize: 11,
        lineHeight: 1.2,
    },

    /* ─── Mermaid diagram image ─── */
    mermaidImageWrapper: {
        marginTop: 14,
        marginBottom: 14,
        alignItems: "center",
        width: "100%",
    },

    /* ─── Mermaid text fallback ─── */
    mermaidFallbackWrapper: {
        marginTop: 12,
        marginBottom: 12,
        padding: 10,
        borderWidth: 1,
        borderColor: "#D9D9D9",
        borderRadius: 4,
        backgroundColor: "#FAFAFA",
    },
    mermaidFallbackHeading: {
        fontSize: 11,
        fontWeight: "bold",
        fontFamily: "CenturyGothic",
        marginBottom: 6,
        color: "#333333",
    },
    mermaidFallbackItem: {
        fontSize: 10,
        fontFamily: "CenturyGothic",
        lineHeight: 1.5,
        color: "#444444",
        paddingLeft: 8,
    },
    mermaidFallbackEmpty: {
        fontSize: 10,
        fontFamily: "CenturyGothic",
        fontStyle: "italic",
        color: "#888888",
    },
});

/* ─── Helpers ─── */

function extractFirstImageUrl(htmlString) {
    const match = htmlString.match(
        /<img\s+(?:[^>]*?\s+)?src\s*=\s*(["'])(.*?)\1/i,
    );
    return match ? match[2] : null;
}

/**
 * Converts a markdown string to an array of react-pdf components.
 */
const markdownToPdfComponents = (markdownText, keyPrefix) => {
    if (!markdownText) return [];

    let htmlContent;
    try {
        htmlContent = marked(markdownText);
    } catch (error) {
        console.error("Error converting markdown to HTML:", error);
        htmlContent = markdownText;
    }

    const minimized = minimizeHtmlSpacing(htmlContent);
    const cleaned = removeHeaderImageBlock(minimized);

    const parsed = new DOMParser().parseFromString(
        `<html><body>${cleaned}</body></html>`,
        "text/html",
    );

    return Array.from(parsed.body.children)
        .filter((node) => node.tagName !== "IMG")
        .map((node, index) => (
            <React.Fragment key={`${keyPrefix}-${index}`}>
                {renderHtmlToReactPdf(node, styles.text)}
            </React.Fragment>
        ));
};

/**
 * Renders a single mermaid diagram result into react-pdf components.
 * Shows the PNG image when available, otherwise a professional text fallback.
 *
 * PDF page content area is ~535pt wide (595pt page - 30pt padding each side).
 * We scale the diagram to fit within this width while preserving aspect ratio.
 */
const PDF_CONTENT_WIDTH = 535;

const renderMermaidDiagram = (segment, index) => {
    if (segment.dataUrl) {
        const svgWidth = segment.width || 800;
        const svgHeight = segment.height || 600;
        const aspectRatio = svgHeight / svgWidth;

        // Scale to fit page width, capped at the original size.
        const displayWidth = Math.min(svgWidth, PDF_CONTENT_WIDTH);
        // Add 25% extra height for a more spacious, professional look.
        const displayHeight = displayWidth * aspectRatio * 1.25;

        return (
            <View key={`mermaid-${index}`} style={styles.mermaidImageWrapper}>
                <Image
                    src={segment.dataUrl}
                    style={{ width: displayWidth, height: displayHeight }}
                />
            </View>
        );
    }

    if (segment.fallback) {
        const { diagramType, relationships } = segment.fallback;
        return (
            <View key={`mermaid-fallback-${index}`} style={styles.mermaidFallbackWrapper}>
                <Text style={styles.mermaidFallbackHeading}>
                    {diagramType || "Architecture Diagram"}
                </Text>
                {relationships.length > 0 ? (
                    relationships.map((rel, idx) => (
                        <Text key={idx} style={styles.mermaidFallbackItem}>
                            {`• ${rel}`}
                        </Text>
                    ))
                ) : (
                    <Text style={styles.mermaidFallbackEmpty}>
                        Diagram content could not be rendered.
                    </Text>
                )}
            </View>
        );
    }

    return null;
};

/* ─── Disclaimer ─── */

const Disclaimer = () => (
    <View style={styles.section}>
        <Text style={styles.disclaimerHeading}>Disclaimer:</Text>
        <Text style={styles.disclaimer}>
            This document is provided for illustrative and demonstration purposes only. It was generated using artificial intelligence technology. As an AI-generated document, it may not fully reflect current industry standards, specific organizational needs, or human expertise.
        </Text>
        <Text style={styles.disclaimer}>
            Users are encouraged to independently verify the accuracy and applicability of this job description to their specific situation before relying on it for any decisions.
        </Text>
        <Text style={styles.disclaimer}>
            SkillsBridge.AI disclaims all liability for any errors, omissions, inaccuracies, or potential biases in this document. Any reliance you place on such information is strictly at your own risk. Unauthorized use, disclosure, or distribution is strictly prohibited.
        </Text>
    </View>
);

/* ─── Main Component ─── */

/**
 * Component to download PDF from Markdown content.
 * Converts markdown to HTML, renders mermaid diagrams as PNG images
 * (with a clean text fallback on failure), and generates the PDF.
 *
 * Diagrams appear in the same position as in the original document.
 */
const SbDownloadMarkdownPdfV2 = ({ markdownContent, jobData: jobDataProp, pdfFileName }) => {
    const selectedJob = useJobsStore((state) => state.selectedJob);
    const jobData = jobDataProp || selectedJob;

    const fileName =
        pdfFileName ||
        `${jobData?.sb_requisition_number || "Position#"}_${jobData?.job_title || "CompanySpecificRoleTitle"}_V${jobData?.version || "Version#"}.pdf`;

    /* Pre-render mermaid diagrams while preserving segment order. */
    const { segments, isReady } = useMermaidPdfImages(markdownContent);

    /* Extract header image from the first markdown segment (if any). */
    const firstMarkdownSegment = segments.find((s) => s.type === "markdown");
    const firstHtml = firstMarkdownSegment?.content ? (() => {
        try { return marked(firstMarkdownSegment.content); } catch { return ""; }
    })() : "";
    const firstImageUrl = extractFirstImageUrl(firstHtml);

    /**
     * Renders every segment in document order:
     *   markdown → html → react-pdf components
     *   mermaid  → PNG image (or text fallback)
     */
    const renderSegments = () =>
        segments.map((segment, idx) => {
            if (segment.type === "markdown") {
                return (
                    <React.Fragment key={`seg-md-${idx}`}>
                        {markdownToPdfComponents(segment.content, `seg-md-${idx}`)}
                    </React.Fragment>
                );
            }
            if (segment.type === "mermaid") {
                return renderMermaidDiagram(segment, idx);
            }
            return null;
        });

    const SbJdDocument = () => (
        <Document>
            <Page style={styles.page}>
                <Text style={styles.watermark} fixed>DRAFT</Text>
                <View style={styles.content}>
                    {firstImageUrl && <Image src={firstImageUrl} />}
                    {renderSegments()}
                    <Disclaimer />
                </View>
            </Page>
        </Document>
    );

    /* Wait for mermaid rendering before exposing the download link. */
    if (!isReady) {
        return <SbButton icon={<DownloadIcon />} disabled />;
    }

    return (
        <PDFDownloadLink document={<SbJdDocument />} fileName={fileName}>
            <SbButton icon={<DownloadIcon />} />
        </PDFDownloadLink>
    );
};

export default SbDownloadMarkdownPdfV2;
